import { Button, SubmitButton } from "@ameelio/ui";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Grid, useMediaQuery as measureScreenWidth } from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";
import { SystemStyleObject, Theme } from "@mui/system";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import addStylesForDevices from "./addStylesForDevices";
import { onlyMobile } from "./responsiveHelpers";

type Props = {
  onBack?: () => void;
  buttonType?: string;
  loading: boolean;
  disabled?: boolean;
  step?: string;
  steps?: string[];
  showSkip?: boolean;
};

const BorderLinearProgress = styled(LinearProgress)(() => ({
  height: 4,
  borderRadius: 5,
  marginBottom: 24,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "#2E47BD60",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: "#2E47BD",
  },
}));

export default function Stepper({
  onBack,
  buttonType,
  loading,
  disabled,
  showSkip,
  step,
  steps,
}: Props) {
  const { t } = useTranslation();
  const mobileOnly = onlyMobile(measureScreenWidth);
  const stepperValue = useMemo(() => {
    if (step && steps) {
      const index = steps.indexOf(step);
      const fraction = (index + 1) / steps.length;
      return fraction * 100;
    }
    // if you don't need progressions then will display a faded line
    return 0;
  }, [step, steps]);

  const styles: SystemStyleObject<Theme> = addStylesForDevices(
    mobileOnly,
    { minWidth: 160 },
    { minWidth: 125 }
  );

  let buttonTextContent = "";
  switch (buttonType) {
    case "start":
      buttonTextContent = t("Start");
      break;
    case "finish":
      buttonTextContent = t("Submit");
      break;
    case "next":
      buttonTextContent = showSkip ? t("Skip") : t("Next");
      break;
    case "close":
      buttonTextContent = t("Close");
      break;
    default:
      buttonTextContent = t("Next");
      break;
  }

  return (
    <>
      <BorderLinearProgress variant="determinate" value={stepperValue} />
      <Grid
        container
        sx={{
          justifyContent: "space-between",
        }}
      >
        <>
          {onBack && (
            <Button
              variant="outlined"
              onClick={onBack}
              sx={styles}
              startIcon={<ArrowBackIcon />}
            >
              {t("Back")}
            </Button>
          )}
          <SubmitButton
            submitting={loading}
            disabledReason={
              disabled
                ? t("Please fill all the required fields to continue")
                : ""
            }
            disabled={disabled}
            sx={styles}
          >
            {buttonTextContent}
          </SubmitButton>
        </>
      </Grid>
    </>
  );
}
